/**
 * AI Studio. Rail · reading column · evidence rail.
 *
 * The thread state lives here rather than in the query cache because a turn is a
 * *conversation event*, not a cacheable resource: it carries the scope it ran under,
 * the trace it produced, and the evidence numbering of the whole thread.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSession, useUser } from "@/app/session.tsx";
import { AgentTrace, type TraceStep } from "@/components/AgentTrace.tsx";
import { DocumentReader, type ReaderTarget } from "@/components/DocumentReader.tsx";
import { Answer, type AnswerTurn } from "@/components/Answer.tsx";
import { Composer } from "@/components/Composer.tsx";
import { EvidenceRail, type EvidenceItem, type ToolEvidence } from "@/components/EvidenceRail.tsx";
import { EmptyState } from "@/components/primitives.tsx";
import { Rail } from "@/components/Rail.tsx";
import { ScopeBar, scopeSentence } from "@/components/ScopeBar.tsx";
import { AddSourcesDialog, mergeSelection } from "@/components/AddSourcesDialog.tsx";
import { SourcesPanel } from "@/components/SourcesPanel.tsx";
import { v2FootText } from "@/components/v2Labels.ts";
import { ApiError, api, isNotFound, streamQuery, streamQueryV2, type V2StreamFrame } from "@/lib/api.ts";
import { citationKey } from "@/lib/api-types.ts";
import {
  conversationMode,
  isV2RouteMissing,
  readV2Message,
  storedQueryMode,
  storeQueryMode,
  V2_SCHEMA,
  v2Status,
  withStreamedCitations,
  type QueryMode,
  type V2AnnotationsFrame,
} from "@/lib/queryV2.ts";
import type {
  AnswerClaim,
  Citation,
  NotebookSourceIssue,
  QueryResponse,
  StreamFrame,
} from "@/lib/api-types.ts";
import type { ClaimMark } from "@/components/AnswerMarkdown.tsx";
import { clsx } from "@/lib/clsx.ts";
import {
  EMPTY_NOTEBOOK_SCOPE,
  isEmptyNotebook,
  notebookScopeFromResponse,
  sameNotebookScope,
  buildQueryBody,
  notebookScopeToRequest,
  notebookTitleFrom,
  toggleExcluded,
  type NotebookScope,
} from "@/lib/notebookScope.ts";

/** A turn with no citations links nothing. Shared so the identity is stable across renders. */
const EMPTY_REFERENCES: Map<string, number> = new Map();

export function WorkspaceRoute() {
  const { t, i18n } = useTranslation();
  const user = useUser();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { conversationId?: string };
  const search = useSearch({ strict: false }) as { collection?: string };

  const [conversationId, setConversationId] = useState<string | null>(params.conversationId ?? null);
  // THE notebook's sources — ONE representation, not two. This used to be mirrored by a
  // second picker-model scope kept in step through a bridge, which existed only because
  // the old tri-state picker spoke a different shape. That picker is gone.
  //
  // EMPTY by default: a notebook is defined by its sources and refuses until it has some,
  // where a conversation started with the whole corpus as its floor. Arriving from a
  // collection seeds THAT COLLECTION ONLY — someone who opened a collection asked about
  // the collection, and adding 3,331 documents behind it would answer from a corpus they
  // did not choose.
  const [notebookScope, setNotebookScope] = useState<NotebookScope>(() =>
    search.collection
      ? { ...EMPTY_NOTEBOOK_SCOPE, collectionIds: [search.collection] }
      : EMPTY_NOTEBOOK_SCOPE,
  );
  const [notebookIssues, setNotebookIssues] = useState<NotebookSourceIssue[]>([]);
  const [turns, setTurns] = useState<AnswerTurn[]>([]);

  // A notebook IS a conversation for now (one thread each), so its id is the conversation
  // id and there is nothing to load until one exists.
  const notebook = useQuery({
    queryKey: ["notebook", conversationId],
    queryFn: () => api.notebook(conversationId as string, user.subject),
    enabled: Boolean(conversationId),
  });

  // A notebook URL opened by an account that does not own it (shared between testers, or
  // a sign-in switch left on the old URL). Without this the page looked like an empty
  // notebook and every question then failed with the same 404.
  const notebookMissing = conversationId !== null && isNotFound(notebook.error);

  // Hydrate from the server ONCE per notebook. This is the reload that never worked: the
  // picker used to come back to its default and the chosen sources were gone.
  const hydrated = useRef<string | null>(null);
  // The in-flight `createNotebook`, so concurrent edits share one notebook.
  const creating = useRef<Promise<string> | null>(null);
  useEffect(() => {
    if (!notebook.data || hydrated.current === notebook.data.id) return;
    hydrated.current = notebook.data.id;
    setNotebookScope(notebookScopeFromResponse(notebook.data));
    setNotebookIssues(notebook.data.issues);
  }, [notebook.data]);

  // The picked documents themselves, so the panel can show titles and group them. Fetched
  // BY ID rather than paged: the corpus is 3,331 documents and a page of 200 would miss
  // most picks.
  const sourceDocuments = useQuery({
    queryKey: ["corpus", "notebook-sources", notebookScope.documentIds],
    queryFn: () => api.corpus({ ids: notebookScope.documentIds, limit: 200 }),
    enabled: notebookScope.documentIds.length > 0,
  });


  const collections = useQuery({ queryKey: ["collections"], queryFn: api.collections });

  /**
   * Persist a source set, CREATING THE NOTEBOOK IF THERE IS NOT ONE YET.
   *
   * Sources are chosen before the first question is asked — that is the whole flow — so
   * waiting for a question to create the notebook meant everything picked beforehand
   * lived only in the browser and was gone on reload. The notebook is cheap and empty
   * until it has sources, so it is created the moment there is something to remember.
   */
  const persistSources = useCallback(
    async (next: NotebookScope) => {
      let id = conversationId;
      // NEVER WRITE A SCOPE WE HAVE NOT LOADED. Switching notebooks changes the id
      // immediately while `notebook` refetches, so for a moment the panel on screen
      // belongs to the previous notebook. A PUT is REPLACE, so a click in that window
      // overwrote the newly-opened notebook's sources with the previous one's.
      if (id && hydrated.current !== id) return;
      if (!id) {
        // ONE creation per notebook, however many edits race into this window. `id` is
        // captured at render, so two toggles inside one round trip both saw `null` and
        // each created a notebook — two rows in the rail, and the first edit written to
        // the one that was then abandoned.
        // NAMED FROM ITS SOURCES. Every notebook was "Notebook tanpa judul", so the rail
        // was a column of identical rows and switching between them — which Scenario 3
        // asks a reader to do — was guesswork.
        const title = notebookTitleFrom(
          next,
          new Map((collections.data ?? []).map((c) => [c.id, c.name])),
          new Map((sourceDocuments.data?.documents ?? []).map((d) => [d.id, d.title])),
        );
        creating.current ??= api.createNotebook(title).then((created) => created.id);
        try {
          id = await creating.current;
        } finally {
          creating.current = null;
        }
        hydrated.current = id;
        setConversationId(id);
        void queryClient.invalidateQueries({ queryKey: ["conversations", user.subject] });
        void navigate({ to: "/studio/$conversationId", params: { conversationId: id } });
      }
      const saved = await api.replaceNotebookSources(
        id,
        user.subject,
        notebookScopeToRequest(next),
      );
      // Adopt what the SERVER resolved, not what was sent: it drops picks this caller
      // cannot see and reports anything that moved or vanished.
      setNotebookScope(notebookScopeFromResponse(saved));
      setNotebookIssues(saved.issues);
      void queryClient.invalidateQueries({ queryKey: ["notebook", id] });
    },
    [collections.data, conversationId, navigate, queryClient, sourceDocuments.data, user.subject],
  );

  /** Tick one document off INSIDE a whole collection.
   *
   * NOT a removal: the document arrived with the collection and was never listed on its
   * own, so an exclusion is the only thing that can say "not this one" while the
   * collection keeps tracking documents added to it later. */
  const toggleExcludedSource = useCallback(
    (documentId: string) => {
      const next = toggleExcluded(notebookScope, documentId);
      setNotebookScope(next);
      void persistSources(next);
    },
    [notebookScope, persistSources],
  );

  /** Change the sources, and save them. */
  const changeSources = useCallback(
    (next: NotebookScope) => {
      setNotebookScope(next);
      if (!sameNotebookScope(next, notebookScope)) void persistSources(next);
    },
    [notebookScope, persistSources],
  );
  const [steps, setSteps] = useState<TraceStep[]>([]);
  const [reader, setReader] = useState<ReaderTarget | null>(null);
  const [traceCollapsed, setTraceCollapsed] = useState(false);
  const [litPassage, setLitPassage] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [renaming, setRenaming] = useState(false);

  /** Save a new name, or clear it when the field is emptied. */
  const commitRename = useCallback(
    async (value: string) => {
      setRenaming(false);
      if (!conversationId) return;
      const next = value.trim();
      if (next === (notebook.data?.title ?? "").trim()) return;
      await api.renameNotebook(conversationId, user.subject, next || null);
      void queryClient.invalidateQueries({ queryKey: ["notebook", conversationId] });
      void queryClient.invalidateQueries({ queryKey: ["conversations", user.subject] });
    },
    [conversationId, notebook.data?.title, queryClient, user.subject],
  );

  const [railOpen, setRailOpen] = useState(false);
  // `busy` is STATE, not a ref read. A ref mutation does not re-render, so deriving it
  // from `abort.current` left the composer stuck on "Kirim" for the whole run and the
  // waiting panel never appeared.
  const [busy, setBusy] = useState(false);
  /** chunk_id -> what the `passage` frames carried: the text, its corpus, its score. */
  const [passageText, setPassageText] = useState<
    Map<string, { text: string; sourceType?: string; score?: number }>
  >(new Map());
  const abort = useRef<AbortController | null>(null);
  const threadEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const next = params.conversationId ?? null;
    setConversationId(next);
    // OPENING A DIFFERENT NOTEBOOK clears what belonged to the last one. Without this the
    // rail kept rendering the previous notebook's sources, thread and banner against the
    // new id for as long as the fetch took — and a click in that window wrote them onto it.
    // `hydrated.current === next` means we just created or answered into this notebook and
    // already hold its state, so there is nothing stale to clear.
    if (next && hydrated.current !== next) {
      setNotebookScope(EMPTY_NOTEBOOK_SCOPE);
      setNotebookIssues([]);
      setTurns([]);
    }
  }, [params.conversationId]);

  const conversations = useQuery({
    queryKey: ["conversations", user.subject],
    queryFn: () => api.conversations(user.subject),
  });
  const corpus = useQuery({
    queryKey: ["corpus", "count"],
    queryFn: () => api.corpus({ limit: 1 }),
  });

  // Replay a conversation opened from the rail. Citations come back on each message,
  // so the evidence rail is reconstructible; the SCOPE is stored nowhere, so a
  // replayed turn says so rather than inventing one.
  const history = useQuery({
    queryKey: ["conversation", conversationId, user.subject],
    queryFn: () => api.conversation(conversationId!, user.subject),
    enabled: conversationId !== null,
  });

  useEffect(() => {
    const data = history.data;
    if (!data) return;
    setTurns((current) => {
      const live = new Map(current.map((turn) => [turn.response?.message_id, turn]));
      const rebuilt: AnswerTurn[] = [];
      const messages = data.messages;
      for (let index = 0; index < messages.length; index += 1) {
        const message = messages[index];
        if (message?.role !== "user") continue;
        const reply = messages[index + 1];
        const synthesis = readSynthesis(reply?.metadata);

        // A turn answered in THIS session keeps its live state — its scope stamp, its
        // trace and its claim count. Replacing it wholesale is what made the stamp
        // read "Dijawab dengan cakupan:" with nothing after the colon.
        const existing = reply ? live.get(reply.id) : undefined;
        const storedV2 = readV2Message(reply?.metadata);
        if (existing && existing.mode === "v2") {
          // The stored claims carry each claim's text and RESOLVED citations, which the
          // stream cannot (its citation frames have no key to join on), so the chips
          // arrive here. A stream that ended before its verdict frame gets its marks
          // from the stored message too: they were persisted before the first byte.
          rebuilt.push({
            ...existing,
            toolsUsed: synthesis.toolsUsed ?? existing.toolsUsed,
            v2: {
              claims: storedV2?.claims ?? existing.v2?.claims,
              faithfulness: storedV2?.faithfulness ?? existing.v2?.faithfulness,
            },
          });
          continue;
        }
        if (storedV2 && !existing) {
          // A Policy Guard block is stored as the refusal text: show the blocked state,
          // as the stream did, not the refusal as if it were an answer.
          const blocked = storedV2.fallbackReason === "policy_blocked";
          rebuilt.push({
            id: message.id,
            question: message.content,
            mode: "v2",
            guardBlocked: blocked ? { reason: reply?.content ?? "" } : undefined,
            response:
              reply && !blocked
                ? ({
                    answer: reply.content,
                    conversation_id: data.id,
                    message_id: reply.id,
                    history_truncated: synthesis.historyTruncated,
                    history_exchanges_in_view: synthesis.historyExchangesInView,
                    citations: reply.source_citations,
                    tool: null,
                    tool_executed: null,
                    fallback_used: storedV2.fallbackUsed,
                    fallback_reason: storedV2.fallbackReason,
                    // v1's gate fields do not exist on v2; never read as a clean gate.
                    dropped_claims_count: -1,
                    provenance_complete: false,
                  } satisfies QueryResponse)
                : undefined,
            v2: { claims: storedV2.claims, faithfulness: storedV2.faithfulness },
            citationIndex: new Map(),
            scopeSentence: "",
            replayed: true,
            toolsUsed: synthesis.toolsUsed,
          });
          continue;
        }
        if (existing) {
          rebuilt.push({
            ...existing,
            toolsUsed: synthesis.toolsUsed ?? existing.toolsUsed,
            // Hydrate durable provenance from stored metadata after the stream finishes.
            response: existing.response
              ? {
                  ...existing.response,
                  claim_map: existing.response.claim_map ?? synthesis.claims,
                  provenance_complete:
                    existing.response.provenance_complete ||
                    (synthesis.provenanceComplete ?? false),
                  history_truncated:
                    synthesis.historyTruncated ?? existing.response.history_truncated,
                  history_exchanges_in_view:
                    synthesis.historyExchangesInView ?? existing.response.history_exchanges_in_view,
                  dropped_claims_count: synthesis.provenanceUnresolved
                    ? -1
                    : (synthesis.droppedClaimsCount ?? existing.response.dropped_claims_count),
                }
              : existing.response,
          });
          continue;
        }

        rebuilt.push({
          id: message.id,
          question: message.content,
          response: reply
            ? ({
                answer: reply.content,
                conversation_id: data.id,
                message_id: reply.id,
                history_truncated: synthesis.historyTruncated,
                history_exchanges_in_view: synthesis.historyExchangesInView,
                citations: reply.source_citations,
                tool: null,
                tool_executed: null,
                fallback_used: synthesis.fallbackUsed ?? false,
                fallback_reason: synthesis.fallbackReason ?? null,
                // From the stored turn's `drop_reasons` when it recorded any, and -1
                // (unknown) otherwise. NEVER 0 as a stand-in for "we did not look":
                // rendering 0 printed "Semua pernyataan didukung sumber" over an answer
                // whose gate had actually failed claims closed, which is the one thing
                // this footer must never say. Absent record and clean gate stay distinct.
                dropped_claims_count: synthesis.provenanceUnresolved
                  ? -1
                  : (synthesis.droppedClaimsCount ?? -1),
                provenance_complete: synthesis.provenanceComplete ?? false,
                // The stored claim-to-passage map, so a replayed turn resolves each
                // claim to the SAME chunk the live response did (#532). Absent on
                // messages stored before the map was persisted.
                claim_map: synthesis.claims,
              } satisfies QueryResponse)
            : undefined,
          citationIndex: new Map(),
          // Replayed, so the scope it ran under is genuinely not recoverable.
          scopeSentence: "",
          replayed: true,
          toolsUsed: synthesis.toolsUsed,
        });
      }
      return rebuilt;
    });
  }, [history.data]);

  // Conversation-wide citation numbering. Restarting at 1 each turn, as the mockups
  // did, means "[2]" refers to two different documents in the same thread.
  const evidence = useMemo(() => {
    const items: EvidenceItem[] = [];
    const seen = new Map<string, number>();
    for (const turn of turns) {
      for (const citation of turn.response?.citations ?? []) {
        // Stored rows can carry `kind:"history"` conversation records: no
        // document, no chunk, no offsets. They are not evidence items —
        // skip them here so they never reach the document card.
        if (citation.kind !== "chunk" && citation.kind !== "tool") continue;
        // KEYED BY `citationKey`, not by `chunk_id`. A live portal read has no chunk id,
        // so keying on it collapsed every tool citation in a conversation onto the single
        // `null` slot — the second one was silently dropped and never rendered.
        const key = citationKey(citation);
        if (seen.has(key)) continue;
        const index = seen.size + 1;
        seen.set(key, index);
        const passage = citation.chunk_id ? passageText.get(citation.chunk_id) : undefined;
        items.push({
          index,
          citation,
          text: passage?.text,
          sourceType: passage?.sourceType,
          score: passage?.score,
        });
      }
    }
    // What the answer's own prose can link to. Two kinds of key, both structural:
    //
    //   "pasal 19"                      -> the citation carrying that legal_reference
    //   "qanun aceh nomor 7 tahun 2019" -> a citation from that document
    //
    // The second exists because the first almost never fires: the model cites the
    // article that contains the rule while the chunks carry whichever articles the
    // retriever returned. The document name is the reference that actually coincides,
    // because an answer opens by naming the instrument it is reading.
    // PER TURN, not conversation-wide. The map is keyed by document name, and a
    // conversation stays on one topic — so a single first-wins map meant every later
    // mention of "Qanun Aceh Nomor 10 Tahun 2018" resolved to whichever card turn 1
    // happened to claim first. Measured in the browser: two turns, two markers, both
    // rendering "Sumber 1", while turn 2's evidence was card 4. The marker pointed at
    // the right DOCUMENT and the wrong PASSAGE — the reader clicks it, lands on
    // unrelated preamble, and a citation apparatus that does that is worse than none.
    //
    // The card NUMBERS stay conversation-wide (see above); only the lookup is scoped,
    // so turn 2's prose resolves against turn 2's own citations.
    const referencesByTurn = new Map<string, Map<string, number>>();
    for (const turn of turns) {
      referencesByTurn.set(turn.id, buildTurnReferences(turn.response?.citations ?? [], seen));
    }
    // The AUTHORITATIVE claim-to-passage marks (#532): each published claim resolved to
    // the rail card(s) of the exact chunk/tool passage that supports it. The document-name
    // references above remain as NAVIGATION — they cannot tell two chunks of one document
    // apart, which is precisely what these can.
    const claimsByTurn = new Map<string, ClaimMark[]>();
    for (const turn of turns) {
      claimsByTurn.set(turn.id, buildClaimMarks(turn.response?.claim_map, seen));
    }
    return { items, index: seen, referencesByTurn, claimsByTurn };
  }, [turns, passageText]);

  const tools = useMemo<ToolEvidence[]>(() => {
    const seen: ToolEvidence[] = [];
    for (const turn of turns) {
      const response = turn.response;
      // A tool answer used to carry `citations: []`, so this card was the ONLY provenance
      // it had. Tool citations exist now (ADR-0041's ADR-0004 gap, closed), and gating on
      // an empty list made this dead code the moment they did — while the citations
      // themselves fell into the "Dari dokumen terindeks" group, telling the reader a live
      // CKAN read was an indexed government document. So: show the card whenever a tool
      // answered and no TOOL citation carries the provenance already.
      const citedTools = (response?.citations ?? []).some((citation) => citation.kind === "tool");
      if (response?.tool && !citedTools) {
        seen.push({ tool: response.tool, executed: response.tool_executed });
      }
    }
    return seen;
  }, [turns]);

  useEffect(() => {
    if (!busy) return;
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length, steps.length]);

  const sentence = scopeSentence(notebookScope, corpus.data?.total ?? null, t, i18n.language);

  // v1 or v2. A conversation belongs to the route that first answered in it (the API
  // answers the other route with 409), so the reader's choice applies only to a notebook
  // nobody has answered in yet; an answered one is locked to its own route.
  const [preferredMode, setPreferredMode] = useState<QueryMode>(storedQueryMode);
  const answeredTurn = turns.find((turn) => turn.response || turn.guardBlocked);
  const lockedMode: QueryMode | null = answeredTurn
    ? (answeredTurn.mode ?? "v1")
    : conversationMode(history.data);
  const mode = lockedMode ?? preferredMode;

  const ask = useCallback(
    async (question: string) => {
      const controller = new AbortController();
      abort.current = controller;
      setBusy(true);
      setSteps([]);
      setElapsed(0);
      setTraceCollapsed(false);
      // The turn's OWN clock. `elapsed` is state, and `ask` is a `useCallback` that does
      // not list it — so the closure holds the value from the render that built it, which
      // is 0, and every finished turn recorded "0 dtk" no matter how long it ran. Adding
      // `elapsed` to the deps would rebuild `ask` once a second instead. The displayed
      // counter stays on state; only the RECORDED duration reads this.
      const startedAt = Date.now();

      const turnId = `turn-${Date.now()}`;
      const askMode = mode;
      setTurns((previous) => [
        ...previous,
        { id: turnId, question, citationIndex: new Map(), scopeSentence: sentence, mode: askMode },
      ]);

      const body = buildQueryBody({
        question,
        userId: user.subject,
        conversationId,
        scope: notebookScope,
      });

      const citations: Citation[] = [];
      const passages = new Map<string, string>();
      let answer = "";
      let messageId = "";
      let newConversationId = conversationId;
      let guardBlocked: { reason: string } | undefined;
      let lateGate = false;
      let dropped = -1;
      let toolsUsed: string[] = [];
      // v2 sends its `annotations` frame BEFORE the text (review C4). The text is still
      // held until the stream ends and then shown with its marks at once: the server
      // buffers the whole answer, so nothing is lost by waiting. Annotations are applied
      // only under the contract this client renders (`schema`), and each streamed claim's
      // citations are joined from the `citation_key` of the citation frames (C5).
      let v2Revealed = false;
      let v2Annotations: V2AnnotationsFrame | undefined;
      let v2Schema: string | undefined;
      const v2CitationsByKey = new Map<string, Citation>();

      try {
        const frames =
          askMode === "v2"
            ? streamQueryV2(body, controller.signal)
            : streamQuery(body, controller.signal);
        for await (const frame of frames) {
          applyFrame(frame);
        }
      } catch (error) {
        if (controller.signal.aborted) {
          patch(turnId, (turn) => ({ ...turn, stopped: true }));
        } else if (askMode === "v2" && isV2RouteMissing(error)) {
          // The server's v2 flag is off. Nothing was answered, so the notebook is not
          // locked: fall back to v1 for the next question and offer to re-ask this one.
          storeQueryMode("v1");
          setPreferredMode("v1");
          patch(turnId, (turn) => ({ ...turn, v2Unavailable: true }));
        } else {
          patch(turnId, (turn) => ({ ...turn, error: describeError(error, t) }));
        }
        abort.current = null;
        setBusy(false);
        return;
      }

      // The stream ended before `done` (a fault, a cut proxy): show what arrived. With no
      // annotations frame the text is UNCHECKED, which renders the "belum diperiksa"
      // banner; the refetch below can still bring the stored marks.
      if (askMode === "v2" && !v2Revealed && answer && !guardBlocked) {
        revealV2();
      }

      abort.current = null;
      setBusy(false);
      setTraceCollapsed(true);
      // Hand the completed trail to the turn, so it survives the answer landing and can
      // be reopened. Reading it back is the point of having recorded it.
      setSteps((finalSteps) => {
        patch(turnId, (turn) => ({
          ...turn,
          steps: finalSteps,
          elapsedSeconds: Math.round((Date.now() - startedAt) / 1000),
        }));
        return finalSteps;
      });
      // Pull the stored message back: the durable claim map, its completeness verdict,
      // and the full tool trace live in `metadata.synthesis`, not the SSE contract.
      if (newConversationId) {
        void queryClient.invalidateQueries({
          queryKey: ["conversation", newConversationId, user.subject],
        });
      }
      if (newConversationId && newConversationId !== conversationId) {
        // WE ALREADY HOLD THIS NOTEBOOK'S STATE. The server created the row for this
        // question; claiming it as hydrated stops the empty `GET /notebooks/{id}` that
        // follows from overwriting the scope the answer was drawn under — which emptied
        // the panel and disabled the composer one beat after the answer appeared.
        hydrated.current = newConversationId;
        setConversationId(newConversationId);
        // The sources were only ever in the browser (seeded from `?collection=`, or picked
        // before the notebook existed). Record them against the notebook the server just
        // made, so a reload finds what the answer was actually scoped to.
        void api
          .replaceNotebookSources(
            newConversationId,
            user.subject,
            notebookScopeToRequest(notebookScope),
          )
          .then((saved) => {
            setNotebookIssues(saved.issues);
            void queryClient.invalidateQueries({ queryKey: ["notebook", newConversationId] });
          });
        void navigate({
          to: "/studio/$conversationId",
          params: { conversationId: newConversationId },
          replace: true,
        });
        void queryClient.invalidateQueries({ queryKey: ["conversations", user.subject] });
      }

      /** The v2 answer as a response; v1's gate fields are absent on v2, never "clean". */
      function v2Response(fallback?: { used: boolean; reason: string | null }): QueryResponse {
        return {
          answer,
          conversation_id: newConversationId ?? "",
          message_id: messageId,
          citations,
          tool: null,
          tool_executed: null,
          fallback_used: fallback?.used ?? false,
          fallback_reason: fallback?.reason ?? null,
          dropped_claims_count: -1,
          provenance_complete: false,
        };
      }

      function revealV2() {
        v2Revealed = true;
        const data = v2Schema === V2_SCHEMA ? v2Annotations : undefined;
        if (!data) {
          // No annotations under a contract we render: the whole answer is unverified.
          patch(turnId, (turn) => ({ ...turn, toolsUsed, response: v2Response(), v2: undefined }));
          return;
        }
        const { claims, fallback_used: used, fallback_reason: reason, ...report } = data;
        // Spans that assert nothing: they carry no mark, and the report already counts them.
        delete report.framing;
        const annotation = {
          claims: withStreamedCitations(Array.isArray(claims) ? claims : [], v2CitationsByKey),
          faithfulness: report,
        };
        const status = v2Status(annotation);
        setSteps((previous) => [
          ...previous,
          {
            call: GUARD_STEP,
            tool: "policy_guard",
            done: true,
            ok: true,
            summary: t("trace.guardPassed"),
          },
          {
            call: GATE_STEP,
            tool: "faithfulness",
            done: true,
            ok: status.kind === "checked" && status.notVerified > 0 ? false : undefined,
            summary: v2FootText(t, status),
          },
        ]);
        patch(turnId, (turn) => ({
          ...turn,
          toolsUsed,
          response: v2Response({ used, reason }),
          v2: annotation,
        }));
      }

      function applyFrame(frame: StreamFrame | V2StreamFrame) {
        if (askMode === "v2") {
          if (frame.event === "answer.delta") {
            answer += frame.data.text;
            return;
          }
          if (frame.event === "annotations") {
            v2Annotations = frame.data;
            return;
          }
          if (frame.event === "message.start") {
            v2Schema = (frame as Extract<V2StreamFrame, { event: "message.start" }>).data.schema;
          }
          if (frame.event === "citation") {
            const key = (frame as Extract<V2StreamFrame, { event: "citation" }>).data.citation_key;
            if (key) v2CitationsByKey.set(key, frame.data);
          }
          if (frame.event === "done" && answer && !guardBlocked) {
            // Reveal before `done` records the history window on the response.
            revealV2();
          }
          if (frame.event === "error") {
            // A conversation answered by the other route, or nothing indexed to answer
            // from: said in words, not as the API's raw message.
            const code = frame.data.code;
            const message =
              code === "http_409"
                ? t("errors.versionConflict")
                : code === "http_404"
                  ? t("errors.queryNotFound")
                  : frame.data.message;
            patch(turnId, (turn) => ({ ...turn, error: message }));
            return;
          }
        }
        switch (frame.event) {
          case "message.start":
            newConversationId = frame.data.conversation_id;
            messageId = frame.data.message_id;
            break;
          case "citation":
            citations.push(frame.data);
            break;
          case "passage": {
            // The words behind a citation, so the reader can check the claim rather
            // than take the reference on trust. A tool result has no chunk id — its
            // provenance is the call, and the evidence rail renders that separately.
            const passage = frame.data;
            if (passage.kind === "chunk") {
              passages.set(passage.chunk_id, passage.text);
              setPassageText((previous) =>
                new Map(previous).set(passage.chunk_id, {
                  text: passage.text,
                  sourceType: passage.source_type,
                  score: passage.score,
                }),
              );
            }
            break;
          }
          case "tool.started":
            // LIVE. The step appears the moment the agent calls the tool, carrying the
            // arguments it was called with — which is what makes a trace readable.
            setSteps((previous) => [
              ...previous,
              {
                call: frame.data.call,
                tool: frame.data.tool,
                arguments: frame.data.arguments,
                done: false,
              },
            ]);
            break;
          case "tool.finished":
            setSteps((previous) =>
              previous.map((step) =>
                step.call === frame.data.call
                  ? {
                      ...step,
                      done: true,
                      ok: frame.data.ok,
                      ms: frame.data.ms,
                      summary: frame.data.summary,
                      passages: frame.data.passages,
                    }
                  : step,
              ),
            );
            break;
          case "trace":
            // The ordered list, for the receipt that survives a reload. The live steps
            // above already showed the work; this is what is left once it is over.
            toolsUsed = frame.data.tools;
            patch(turnId, (turn) => ({ ...turn, toolsUsed: frame.data.tools }));
            break;
          case "guard.blocked":
            guardBlocked = { reason: frame.data.reason };
            // The guard is a step in the trail, like any other work the system did.
            // DERIVED from a real frame, never invented: this only appears because a
            // `guard.blocked` arrived.
            setSteps((previous) => [
              ...previous,
              {
                call: GUARD_STEP,
                tool: "policy_guard",
                done: true,
                ok: false,
                summary: t("trace.guardBlocked"),
              },
            ]);
            patch(turnId, (turn) => ({ ...turn, guardBlocked }));
            break;
          case "answer.delta":
            answer += frame.data.text;
            patch(turnId, (turn) => ({
              ...turn,
              toolsUsed,
              response: {
                answer,
                conversation_id: newConversationId ?? "",
                message_id: messageId,
                citations,
                tool: null,
                tool_executed: null,
                fallback_used: false,
                fallback_reason: null,
                // -1 until a `faithfulness` frame says how many submitted records were
                // dropped. Complete claim provenance arrives later from the stored-message
                // refetch, never from SSE.
                dropped_claims_count: dropped,
                provenance_complete: false,
              },
            }));
            break;
          case "faithfulness": {
            const data = frame.data as Extract<StreamFrame, { event: "faithfulness" }>["data"];
            // Two steps land together here, and both are facts on the wire: the guard
            // PASSED (we are receiving answer text at all, which only happens past it)
            // and the source check returned this verdict.
            setSteps((previous) => [
              ...previous,
              {
                call: GUARD_STEP,
                tool: "policy_guard",
                done: true,
                ok: true,
                summary: t("trace.guardPassed"),
              },
              {
                call: GATE_STEP,
                tool: "faithfulness",
                done: true,
                // Zero means no submitted record was dropped. The post-answer metadata
                // refetch is what can later prove complete answer provenance.
                ok: data.dropped > 0 ? false : undefined,
                summary:
                  data.dropped > 0
                    ? t("answer.dropped", { count: data.dropped })
                    : t("answer.gateUnknown"),
              },
            ]);
            // The gate lands last and can only annotate: text already on screen
            // cannot be unshown, so an unsupported claim is MARKED, not deleted.
            lateGate = data.dropped > 0;
            dropped = data.dropped;
            patch(turnId, (turn) => ({
              ...turn,
              lateGate,
              response: turn.response
                ? {
                    ...turn.response,
                    dropped_claims_count: data.dropped,
                    fallback_used: data.fallback_used,
                    fallback_reason: data.fallback_reason,
                  }
                : turn.response,
            }));
            break;
          }
          case "error":
            patch(turnId, (turn) => ({ ...turn, error: frame.data.message }));
            break;
          case "done":
            patch(turnId, (turn) => ({
              ...turn,
              historyWindow: {
                history_truncated: frame.data.history_truncated,
                history_exchanges_in_view: frame.data.history_exchanges_in_view,
              },
              response: turn.response
                ? {
                    ...turn.response,
                    history_truncated: frame.data.history_truncated,
                    history_exchanges_in_view: frame.data.history_exchanges_in_view,
                  }
                : undefined,
            }));
            break;
        }
      }
    },
    [conversationId, mode, navigate, queryClient, notebookScope, sentence, t, user.subject],
  );

  function patch(id: string, update: (turn: AnswerTurn) => AnswerTurn) {
    setTurns((previous) => previous.map((turn) => (turn.id === id ? update(turn) : turn)));
  }

  async function newChat() {
    setTurns([]);
    setSteps([]);
    setNotebookIssues([]);
    // EMPTY, not the whole corpus: a notebook is defined by its sources and refuses
    // until it has some. Carrying the last notebook's scope forward is how someone
    // asks a broad question inside a single-document scope and reads the refusal as
    // the corpus being empty.
    setNotebookScope(EMPTY_NOTEBOOK_SCOPE);
    // CREATED NOW, so the click visibly does something: deferring it left the rail
    // unchanged and the reader with nowhere to type a name. It starts untitled and
    // the header title is editable, so it can be named immediately — and if it is
    // not, adding sources names it after them.
    const created = await api.createNotebook();
    hydrated.current = created.id;
    setConversationId(created.id);
    void queryClient.invalidateQueries({ queryKey: ["conversations", user.subject] });
    void navigate({
      to: "/studio/$conversationId",
      params: { conversationId: created.id },
    });
  }

  return (
    <div className={clsx("app", railOpen && "rail-open", reader && "reader-open")}>
      <a className="skip-link" href="#thread">
        {t("nav.skipToContent")}
      </a>

      <Rail
        conversations={conversations.data ?? []}
        activeConversationId={conversationId}
        onNewNotebook={newChat}
        sourcesPanel={
          <SourcesPanel
            collections={collections.data ?? []}
            documents={sourceDocuments.data?.documents ?? []}
            wholeCorpus={notebookScope.wholeCorpus}
            wholeCollectionIds={notebookScope.collectionIds}
            excludedDocumentIds={notebookScope.excludedDocumentIds}
            corpusTotal={corpus.data?.total ?? 0}
            issues={notebookIssues}
            onAddSources={() => setAddOpen(true)}
            onDetachWholeCorpus={() => changeSources({ ...notebookScope, wholeCorpus: false })}
            onRemoveCollection={(id) =>
              changeSources({
                ...notebookScope,
                collectionIds: notebookScope.collectionIds.filter((value) => value !== id),
              })
            }
            onToggleExcluded={toggleExcludedSource}
            onRemoveDocument={(id) =>
              changeSources({
                ...notebookScope,
                documentIds: notebookScope.documentIds.filter((value) => value !== id),
              })
            }
          />
        }
        user={user}
        onSignOut={() => void signOut()}
      />

      <main className="column">
        <div className="col-head">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setRailOpen((open) => !open)}
            aria-label={t("nav.conversations")}
          >
            ☰
          </button>
          {/* WHICH NOTEBOOK AM I IN, and click it to rename. The rail names them, but the
              reading column said only "AI Studio", so after clicking between notebooks
              nothing on screen told you where you had landed — and there was no way to
              name one at all. */}
          {conversationId && !notebookMissing ? (
            renaming ? (
              <input
                className="input col-title-input"
                autoFocus
                defaultValue={notebook.data?.title ?? ""}
                placeholder={t("notebook.namePlaceholder")}
                onBlur={(event) => void commitRename(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void commitRename(event.currentTarget.value);
                  if (event.key === "Escape") setRenaming(false);
                }}
              />
            ) : (
              <button
                type="button"
                className="col-title col-title-button"
                onClick={() => setRenaming(true)}
                title={t("notebook.rename")}
              >
                {notebook.data?.title?.trim() || t("notebook.untitled")}
              </button>
            )
          ) : (
            <span className="col-title">{t("nav.studio")}</span>
          )}
          <label
            className="answer-mode"
            title={
              lockedMode
                ? t("answerV2.modeLocked", {
                    mode: lockedMode === "v2" ? t("answerV2.modeV2") : t("answerV2.modeV1"),
                  })
                : undefined
            }
          >
            {t("answerV2.mode")}
            <select
              value={mode}
              disabled={busy || lockedMode !== null}
              onChange={(event) => {
                const next = event.currentTarget.value === "v2" ? "v2" : "v1";
                storeQueryMode(next);
                setPreferredMode(next);
              }}
            >
              <option value="v1">{t("answerV2.modeV1")}</option>
              <option value="v2">{t("answerV2.modeV2")}</option>
            </select>
          </label>
        </div>

        <div className="thread" id="thread">
          <div className="thread-inner">
            {notebookMissing ? (
              <EmptyState
                title={t("notebook.notFoundTitle")}
                body={t("notebook.notFoundBody")}
                action={
                  <button type="button" className="btn btn-primary" onClick={() => void newChat()}>
                    {t("nav.newNotebook")}
                  </button>
                }
              />
            ) : turns.length === 0 && !busy ? (
              <EmptyState title={t("chat.emptyTitle")} body={t("chat.emptyBody")} />
            ) : null}

            {turns.map((turn) => (
              <Answer
                key={turn.id}
                turn={turn}
                litPassage={litPassage}
                references={evidence.referencesByTurn.get(turn.id) ?? EMPTY_REFERENCES}
                claims={evidence.claimsByTurn.get(turn.id)}
                evidenceIndex={evidence.index}
                onNewChat={newChat}
                newChatDisabled={busy}
                onAskV1={turn.v2Unavailable ? () => void ask(turn.question) : undefined}
                onPassageClick={(index) => {
                  setLitPassage(index);
                  // Bring the cited passage into view. Hover-only linking left a reader
                  // clicking a chip while the card sat off-screen.
                  if (index !== null) {
                    document.getElementById(`ev-${index}`)?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }
                }}
              />
            ))}

            {busy ? (
              <>
                <AgentTrace
                  steps={steps}
                  elapsedSeconds={elapsed}
                  running={busy}
                  collapsed={traceCollapsed}
                  onToggle={() => setTraceCollapsed((collapsed) => !collapsed)}
                />
                <p className="hint">{t("chat.waitingHelp")}</p>
              </>
            ) : null}
            <div ref={threadEnd} />
          </div>
        </div>

        <div className="dock">
          <div className="dock-inner">
            <ScopeBar
              scope={notebookScope}
              corpusTotal={corpus.data?.total ?? null}
              onEdit={() => setAddOpen(true)}
            />
            <Composer
              onSubmit={(text) => void ask(text)}
              onStop={() => {
                abort.current?.abort();
                abort.current = null;
                setBusy(false);
              }}
              busy={busy}
              disabled={notebookMissing || isEmptyNotebook(notebookScope)}
              disabledReason={notebookMissing ? t("notebook.notFoundBody") : t("scope.emptyHelp")}
            />
          </div>
        </div>
      </main>

      <EvidenceRail
        items={evidence.items}
        tools={tools}
        litIndex={litPassage}
        onFocus={setLitPassage}
        // TWO different questions, so two different actions. Clicking the citation
        // asks "what did the answer quote?"; "Buka dokumen asli" asks "what does the
        // document say?". One pane, opened in the mode that was asked for.
        onOpenPassage={(item) =>
          setReader({ citation: item.citation, passage: item.text, mode: "passage" })
        }
        onOpenDocument={(item) =>
          setReader({ citation: item.citation, passage: item.text, mode: "original" })
        }
      />

      <DocumentReader
        target={reader}
        onClose={() => setReader(null)}
        onOpenOriginal={() => setReader((current) => (current ? { ...current, mode: "original" } : current))}
      />

      <AddSourcesDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          collections={collections.data ?? []}
          corpusTotal={corpus.data?.total ?? 0}
          current={{
            wholeCorpus: notebookScope.wholeCorpus,
            collectionIds: notebookScope.collectionIds,
            documentIds: notebookScope.documentIds,
          }}
          onAdd={(added) => {
            const merged = mergeSelection(
              {
                wholeCorpus: notebookScope.wholeCorpus,
                collectionIds: notebookScope.collectionIds,
                documentIds: notebookScope.documentIds,
              },
              added,
            );
            changeSources({ ...notebookScope, ...merged });
          }}
        />


    </div>
  );
}

/** Every documented error status, said in words a reader can act on. */
// Synthetic call indexes for the two non-tool steps, chosen above MAX_TOOL_CALLS so
// they can never collide with a real call index.
const GUARD_STEP = 1000;
const GATE_STEP = 1001;

export function describeError(error: unknown, t: (key: string) => string): string {
  if (!(error instanceof ApiError)) return t("errors.network");
  switch (error.status) {
    // 400 is ambiguous the same way 404 is: an empty question and a notebook whose
    // sources have all vanished share it. Naming both beats asserting the wrong one —
    // and the composer is already disabled for the empty-notebook case the user can see,
    // so this fires when the browser's view has gone stale.
    case 400:
      return t("errors.badRequest");
    case 401:
      return t("errors.unauthorized");
    // 404 is deliberately ambiguous on this API: an unknown conversation and a corpus
    // with nothing indexed are the same response, so guessing between them would be
    // making something up.
    case 404:
      return t("errors.queryNotFound");
    // A conversation belongs to the route (v1 or v2) that first answered in it.
    case 409:
      return t("errors.versionConflict");
    case 413:
      return t("errors.tooLarge");
    case 502:
      return t("errors.upstream");
    default:
      return error.message || t("errors.generic");
  }
}

/**
 * `messages[].metadata.synthesis` is where the agent's own account of a turn lives —
 * `tools_used` (the ordered tool trace) and the faithfulness block. The `/query`
 * response returns only `tool` and `tool_executed`, so this is the ONLY place the
 * full round trace survives a page reload.
 */
export function readSynthesis(metadata: Record<string, unknown> | undefined): {
  toolsUsed?: string[];
  fallbackUsed?: boolean;
  fallbackReason?: string | null;
  droppedClaimsCount?: number;
  provenanceComplete?: boolean;
  provenanceUnresolved?: boolean;
  historyTruncated?: boolean;
  historyExchangesInView?: number;
  /** The stored claim-to-passage map (#532) — `undefined` on pre-#532 messages, so an
   *  absent record stays distinct from a turn whose map is genuinely empty. */
  claims?: AnswerClaim[];
} {
  const conversation = metadata?.conversation as
    | { history_truncated?: unknown; history_exchanges_in_view?: unknown }
    | undefined;
  const historyWindow = {
    historyTruncated:
      typeof conversation?.history_truncated === "boolean"
        ? conversation.history_truncated
        : undefined,
    historyExchangesInView:
      typeof conversation?.history_exchanges_in_view === "number" &&
      Number.isInteger(conversation.history_exchanges_in_view) &&
      conversation.history_exchanges_in_view >= 0
        ? conversation.history_exchanges_in_view
        : undefined,
  };
  const synthesis = metadata?.synthesis as
    | {
        tools_used?: unknown;
        claims?: unknown;
        faithfulness?: {
          fallback_used?: unknown;
          fallback_reason?: unknown;
          drop_reasons?: unknown;
          provenance_complete?: unknown;
          provenance_failure?: unknown;
        };
      }
    | undefined;
  if (!synthesis) return conversation ? historyWindow : {};
  const toolsUsed = Array.isArray(synthesis.tools_used)
    ? synthesis.tools_used.filter((tool): tool is string => typeof tool === "string")
    : undefined;
  // The backend's verdict, not a re-derivation from the counts. Since #622's follow-up a
  // record that failed placement (`unplaceable_claim` / `surplus_excerpt`) is an ordinary
  // drop on a published answer — it licensed nothing and the coverage judge passed what
  // survived — so its count is a number to render, not a reason to say "unknown". Only
  // `provenance_failure` (a fabricated citation, or a coverage refusal) means the row
  // cannot honestly report a count.
  const provenanceFailure = synthesis.faithfulness?.provenance_failure;
  const provenanceUnresolved = typeof provenanceFailure === "string" && provenanceFailure.length > 0;
  return {
    ...historyWindow,
    toolsUsed,
    claims: readStoredClaims(synthesis.claims),
    fallbackUsed:
      typeof synthesis.faithfulness?.fallback_used === "boolean"
        ? synthesis.faithfulness.fallback_used
        : undefined,
    fallbackReason:
      typeof synthesis.faithfulness?.fallback_reason === "string"
        ? synthesis.faithfulness.fallback_reason
        : null,
    droppedClaimsCount: provenanceUnresolved
      ? undefined
      : readDroppedCount(synthesis.faithfulness?.drop_reasons),
    provenanceComplete:
      typeof synthesis.faithfulness?.provenance_complete === "boolean"
        ? synthesis.faithfulness.provenance_complete
        : undefined,
    provenanceUnresolved,
  };
}

/**
 * How many claims the gate failed, from the stored turn — or `undefined` when the row
 * genuinely does not say.
 *
 * `drop_reasons` is a map of reason to COUNT (`{"unsupported": 4}`), and it survives the
 * read-back redaction: `_PRE_GUARD_SYNTHESIS_KEYS` withholds `dropped_claims` and
 * `unparsed_completion` — the pre-Policy-Guard SENTENCES — and nothing else (ADR-0008,
 * `api/app.py:_redacted_message_metadata`). So the count is available to a replayed turn
 * while the text it counts stays withheld, which is the whole point: a reader learns that
 * four claims were unsupported without being served four un-guarded model sentences.
 *
 * AN EMPTY MAP IS ZERO, NOT UNKNOWN. `drop_reasons` is written on every agent-loop turn —
 * verified against the local corpus, where three clean answers stored `{}` and the fourth
 * stored `{"unsupported": 4}`. Reading `{}` as unknown would therefore relabel every
 * correctly-grounded answer "Hasil pemeriksaan … tidak tersimpan", which is the same defect
 * as the one being fixed, pointed the other way.
 *
 * A MISSING key still returns `undefined`. That is a turn written by a path which recorded
 * no verdict, and an absent record is not a clean gate.
 */
function readDroppedCount(dropReasons: unknown): number | undefined {
  if (!dropReasons || typeof dropReasons !== "object" || Array.isArray(dropReasons)) {
    return undefined;
  }
  return Object.values(dropReasons as Record<string, unknown>)
    .filter(
      (value): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0,
    )
    .reduce((total, value) => total + value, 0);
}

/**
 * The stored `metadata.synthesis.claims`, kept only when it has the published shape.
 *
 * Shape-checked field by field because this crosses the API boundary from a JSON blob:
 * a malformed entry is DROPPED rather than rendered as a marker pointing nowhere.
 * A current complete map contains only resolved citations. The nullable shape remains a
 * defensive read boundary for legacy or malformed stored metadata; such a map cannot
 * produce the backend completeness verdict required by the supported footer.
 */
function readStoredClaims(value: unknown): AnswerClaim[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const claims: AnswerClaim[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const { claim, citations } = item as { claim?: unknown; citations?: unknown };
    if (typeof claim !== "string" || !Array.isArray(citations)) continue;
    const refs = citations
      .filter((ref): ref is Record<string, unknown> => Boolean(ref) && typeof ref === "object")
      .map((ref) => ({
        citation_key: String(ref.citation_key ?? ""),
        citation:
          ref.citation && typeof ref.citation === "object"
            ? (ref.citation as unknown as Citation)
            : null,
      }));
    claims.push({ claim, citations: refs });
  }
  return claims;
}

/**
 * Published claims -> the rail card(s) holding the exact passage each one cites.
 *
 * This is the AUTHORITATIVE marker source (#532): it resolves through the citation the
 * gate attached to each claim — chunk id, or tool + call — never through a document
 * name, so two claims supported by two chunks of the SAME document each reach their own
 * card. A fabricated key (`citation: null`) and a citation whose card is not on the
 * rail both link nothing rather than guessing. Multi-passage claims keep every card.
 */
export function buildClaimMarks(
  claimMap: ReadonlyArray<AnswerClaim> | undefined,
  indexByKey: ReadonlyMap<string, number>,
): ClaimMark[] {
  const marks: ClaimMark[] = [];
  for (const claim of claimMap ?? []) {
    if (!claim.claim) continue;
    const indexes: number[] = [];
    for (const ref of claim.citations) {
      if (!ref.citation) continue;
      const index = indexByKey.get(citationKey(ref.citation));
      if (index !== undefined && !indexes.includes(index)) indexes.push(index);
    }
    if (indexes.length > 0) marks.push({ text: claim.claim, indexes });
  }
  return marks;
}

/**
 * ONE TURN's prose-to-evidence lookup: what that turn's own answer can link to.
 *
 * NON-AUTHORITATIVE NAVIGATION ONLY (#532). This keys on document names and legal
 * references, so it structurally CANNOT distinguish two chunks of one document —
 * first-wins within a turn is the best it can do. The claim's actual provenance is
 * `buildClaimMarks` above, which resolves through the citation the gate attached to
 * each claim; this map remains only so a document name in the prose still reaches
 * that document's card.
 *
 * Scoped per turn, deliberately. The keys are document names and a conversation stays
 * on one topic, so a single conversation-wide first-wins map meant every later mention
 * of "Qanun Aceh Nomor 10 Tahun 2018" resolved to whichever card turn 1 claimed first.
 * Observed in the browser: two turns, both markers rendering "Sumber 1", while turn 2's
 * evidence was card 4 — the right DOCUMENT, the wrong PASSAGE. A reader who checks that
 * marker lands on unrelated preamble, which is worse than no marker at all.
 *
 * The card NUMBERS stay conversation-wide (restarting them per turn would make "2" mean
 * two different documents in one thread); only this lookup is scoped.
 *
 * `indexByChunkId` is that conversation-wide numbering. A citation whose chunk is not in
 * it links nothing rather than guessing.
 */
export function buildTurnReferences(
  citations: ReadonlyArray<Citation>,
  indexByChunkId: ReadonlyMap<string, number>,
): Map<string, number> {
  const references = new Map<string, number>();
  for (const citation of citations) {
    const index = indexByChunkId.get(citationKey(citation));
    if (index === undefined) continue;
    const reference = citation.legal_reference?.replace(/\s+/g, " ").trim().toLowerCase();
    if (reference && !references.has(reference)) references.set(reference, index);
    for (const key of documentReferenceKeys(citation.document_title)) {
      if (!references.has(key)) references.set(key, index);
    }
  }
  return references;
}

/**
 * The ways an answer might name the document a citation came from.
 *
 * Corpus titles are filename-derived slugs — `qanun-aceh-7-2019-keterbukaan-informasi-publik`
 * — while the model writes "Qanun Aceh Nomor 7 Tahun 2019". Both describe the same
 * instrument, so the slug is turned into the phrasings prose actually uses.
 *
 * Deliberately narrow: it fires only on a `<kind> <number> <year>` slug, so a title
 * that is not a numbered instrument produces no keys and links nothing. A near-miss
 * would put a citation chip on the wrong document, which is worse than no chip.
 */
export function documentReferenceKeys(title: string): string[] {
  const match = /^(qanun|pergub|perpres|permendagri|undang-undang)[-\s]*(?:aceh[-\s]*)?(\d+)[-\s]+(\d{4})/i.exec(
    title.trim(),
  );
  if (!match) return [];
  const [, rawKind, number, year] = match;
  const kind = (rawKind ?? "").toLowerCase();
  const aceh = /aceh/i.test(title) ? " aceh" : "";
  const label = kind === "undang-undang" ? "undang-undang" : `${kind}${aceh}`;
  return [
    `${label} nomor ${number} tahun ${year}`,
    `${label} ${number} tahun ${year}`,
    `${label} ${number}/${year}`,
  ];
}
